import { getChartDataNew } from "@/controllers/chart-data";
import { getStockTradingSignalsFlags } from "@/controllers/stock-trading-signals";
import { negativeState, positiveState } from "@/lib/store";
import { Stock } from "@/models/stock";
import dayjs from "dayjs";
import { useEffect, useMemo, useRef, useState } from "react";
import { useAtomValue } from "jotai";
import type { ChartEvent } from "@ortex-charts/financial";
import type { Bar } from "@ortex-charts/math";
import TimeSeriesChart, { type TimeSeries, type TimeSeriesChartHandle } from "@/components/ortex-charts/TimeSeriesChart";
import { formatDate } from "@/lib/utils";
import styles from "./StockTradingSignalsChart.module.scss";

/** One signal as the flags endpoint and the table describe it. */
export interface SignalFlag {
    x: number;
    id?: number | string;
    row_id?: number | string;
    ["b/s"]?: "buy" | "sell" | string;
    days?: number;
    significance?: number;
    sip_id?: number | string;
    return?: number;
    type?: string;
}

interface StockTradingSignalsChartProps {
    appliedFilters?: { items: any[]; page: number };
    stock: Stock;
    type: "historical" | "latest";
    /** Earliest signal date the user added; the price history starts three months before it. */
    newDate?: number;
    /** Signals to mark; fetched for the last twelve months when omitted (custom views). */
    flags?: SignalFlag[];
    /** A signal time to centre a one-month window on. */
    focusTime?: number | null;
}

const stars = (significance: number | undefined): string => {
    const n = Math.max(0, Math.min(5, Math.round(((significance ?? 0) / 100) * 5)));
    return "★".repeat(n) + "☆".repeat(5 - n);
};

const initials = (type: string | undefined) =>
    (type ?? "")
        .split("-")
        .map((word) => word.charAt(0))
        .join("") || "•";

/** Signals as badges on the candles: buy in the positive colour, sell in the negative, the rating in the tooltip. */
export function signalEvents(flags: SignalFlag[] | undefined, positive: string, negative: string): ChartEvent[] {
    return (flags ?? [])
        .filter((f) => f && f.x)
        .map((f, i) => ({
            id: String(f.sip_id ?? f.id ?? `signal-${i}`),
            time: f.x,
            style: "badge",
            text: initials(f.type),
            color: f["b/s"] === "sell" ? negative : positive,
            position: f["b/s"] === "sell" ? "belowBar" : "aboveBar",
            group: f.type,
            tooltip: [
                `${f.type ?? "Signal"} on the day for ${formatDate(dayjs(f.x))}`,
                `Significance: ${stars(f.significance)} ${f.significance != null ? `${Math.round(f.significance)}%` : ""}`.trim(),
                f.days != null && f.return != null ? `Hold for ${f.days} days for ${f.return.toFixed(2)}% profit` : "",
            ].filter(Boolean),
        }));
}

const StockTradingSignalsChart = ({ appliedFilters, stock, type, newDate, flags, focusTime }: StockTradingSignalsChartProps) => {
    const positive = useAtomValue(positiveState);
    const negative = useAtomValue(negativeState);
    const chartRef = useRef<TimeSeriesChartHandle>(null);
    const [bars, setBars] = useState<Bar[]>([]);
    // Custom views mount the chart without a parent: the flags come from the endpoint instead.
    const [localFlags, setLocalFlags] = useState<SignalFlag[]>([]);

    useEffect(() => {
        if (appliedFilters !== undefined && appliedFilters.page <= 0) return;
        let alive = true;
        const from = newDate > 0 ? dayjs(newDate).subtract(3, "months") : dayjs().subtract(12, "months");
        getChartDataNew(stock.tradingitem_id, "ohlcv", from.format("YYYY-MM-DD"), dayjs().format("YYYY-MM-DD")).then((data) => {
            if (alive) setBars((data ?? []).map(([time, open, high, low, close, volume]) => ({ time, open: open || close, high: high || close, low: low || close, close, volume: volume ?? 0 })));
        });
        return () => {
            alive = false;
        };
    }, [appliedFilters, stock.stock_id, type, newDate]);

    useEffect(() => {
        if (flags !== undefined) return;
        getStockTradingSignalsFlags(stock.stock_id, {}).then((all) => setLocalFlags((all as SignalFlag[]).filter((flag) => flag.x > dayjs().subtract(12, "months").valueOf())));
    }, [stock.stock_id, flags]);

    const series = useMemo<TimeSeries[]>(
        () => [
            { id: "price", title: `${stock.ticker} Price in ${stock.currency}`, type: "candlestick", data: bars, precision: 2, upColor: positive, downColor: negative },
            { id: "volume", title: `${stock.ticker} Volume`, type: "column", data: bars.map((b) => [b.time, b.volume] as [number, number]), color: "dodgerblue", pane: "volume", format: "compact", precision: 0 },
        ],
        [bars, stock.ticker, stock.currency, positive, negative]
    );
    const events = useMemo(() => signalEvents(flags ?? localFlags, positive, negative), [flags, localFlags, positive, negative]);

    useEffect(() => {
        if (!focusTime || !bars.length) return;
        const half = 15 * 86_400_000;
        chartRef.current?.setRange(focusTime - half, focusTime + half);
    }, [focusTime, bars.length]);

    return (
        <div style={{ overflow: "hidden" }} className={styles["chart-container"]} key={type}>
            <TimeSeriesChart
                ref={chartRef}
                series={series}
                events={events}
                paneHeights={{ volume: 0.25 }}
                initialRange={183}
                rangeButtons={[
                    { label: "1m", days: 31 },
                    { label: "3m", days: 92 },
                    { label: "6m", days: 183 },
                    { label: "All", days: 0 },
                ]}
                legend={false}
                exportName={`${stock.ticker}-signals-${type}`}
            />
        </div>
    );
};

export default StockTradingSignalsChart;
